import { Component, OnInit } from '@angular/core';
import { ColSpanParams, GridOptions } from 'ag-grid-community';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { BPProfilesService, BPProfilesViewCode } from '../../bp-profiles.service';
import { IColDef } from '../../../../shared/report/report-grid.service';
import { BPProfilesDocumentsDataService, IDocumentInfo, IDocumentType } from './bp-profiles-documents-data.service';
import { util } from '../../../../shared/util';
import { CellRendererComp } from '../../../../shared/ag-grid-components/ga-ag-grid.component';
import { IAgButtonDropdownRendererParams } from '../../../../shared/ag-grid-components/ag-button-dropdown/ag-button-dropdown-renderer.component';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '../../../../shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { IDocument } from '../../../../../type_definitions/datatypes';
import { TranslationService } from '../../../../shared/translation/translation.service';
import _ from 'underscore';
import { StateService } from '@uirouter/angular';
import { STATE_BPPROFILES_CAVEATS } from '../../../../shared/app-states';
import { DialogBusyButtonFn, DialogSize, GaMessageService, IDialog, IDialogButton } from '../../../../shared/message/ga-message.service';
import { AddEditDocumentDialogComponent } from './add-edit-document-dialog.component';
import { DocumentTypeCode } from '../../../../shared/document/document-data.service';
import { AgGridDomLayout, GaAgGridService } from '../../../../shared/ag-grid-components/ga-ag-grid.service';
import { GRID_BP_DOCUMENTS } from '@app/shared/app-grids';

enum GridColumn {
  OPTIONS = 'options',
  DOCUMENT = 'document',
  TYPE = 'type'
}

@Component({
  selector: 'bp-profiles-documents',
  template: `
    <button class="btn btn-primary margin-bottom-sm add-document-btn" translate [translateParams]="{ label: entityLabel }" (click)="onAddDocument()" *ngIf="hasWritePermission">GA.BUTTONS.ADD_LABEL</button>

    <div class="row missing-document-buttons" *ngIf="documentInfo && hasWritePermission">
      <div class="col-sm-10">
        <button class="btn btn-default margin-right-xs margin-bottom-xs" *ngFor="let doc of documentInfo?.missingDocumentTypes" translate [translateParams]="{ label: doc.description }" (click)="onAddDocument(doc)">GA.BUTTONS.ADD_LABEL</button>
      </div>
    </div>
    
    <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit"></ga-ag-grid>
  `
})
export class BPProfilesDocumentsComponent implements OnInit {
  public gridOptions:GridOptions;
  public gridInit:boolean;
  public gridId:string = GRID_BP_DOCUMENTS;
  public hasWritePermission:boolean;
  public documentInfo:IDocumentInfo;
  public entityLabel:string;

  private backupMissingDocumentTypes:IDocumentType[];

  constructor(
    private authService:AuthService,
    private bpProfilesDocumentsDataService:BPProfilesDocumentsDataService,
    private bpProfilesService:BPProfilesService,
    private translationService:TranslationService,
    private stateService:StateService,
    private messageService:GaMessageService,
    private gaAgGridService:GaAgGridService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.entityLabel = this.translationService.instant('BPPROFILES.DOCUMENTS.DOCUMENT.LABEL');
    this.hasWritePermission = this.authService.viewWrite(BPProfilesViewCode.DOCUMENTS);

    // initialize grid
    this.gridInitialize();
  }

  private getColDefs():IColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumn.OPTIONS,
        headerName: '',
        sortable: false,
        pinned: 'left',
        width: 46,
        suppressHeaderMenuButton: true,
        cellClass: 'cell-actions text-center ag-button-dropdown-renderer',
        cellRenderer: CellRendererComp.BUTTON_DROPDOWN_RENDERER,
        cellRendererParams: <IAgButtonDropdownRendererParams>{
          dropdownMenus: [
            { label: 'GA.BUTTONS.EDIT', onClick: this.onEditDocument, iconClass: 'fa-pencil' },
            { label: 'GA.BUTTONS.DELETE', onClick: this.onDeleteDocument, iconClass: 'fa-trash-o' }
          ],
          hideInRowPinned: true
        },
        suppressSizeToFit: true,
        suppressAutoSize: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        hide: !this.hasWritePermission
      },
      {
        field: GridColumn.DOCUMENT,
        headerName: 'BPPROFILES.DOCUMENTS.DOCUMENT.LABEL',
        valueGetter: 'data.description',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            if (params.node.isRowPinned()) {
              const label:string = this.translationService.instant('BPPROFILES.DOCUMENTS.TOTAL_CAVEATS', { count: params.data?.caveatsCount });
              return [{
                label,
                title: label,
                clickFn: (event:Event):void => {
                  event.preventDefault();
                  this.stateService.go(STATE_BPPROFILES_CAVEATS);
                }
              }];
            } else {
              return [{
                label: params.value,
                href: this.bpProfilesDocumentsDataService.getAuthenticatedDocumentUrl(this.bpProfilesService.activeProfile?.id, (<IDocument>params.data).id)
              }];
            }
          },
          type: RichTextType.LINK
        },
        colSpan: (params:ColSpanParams):number => params.node.isRowPinned()? 2 : 1
      },
      {
        field: GridColumn.TYPE,
        headerName: 'BPPROFILES.DOCUMENTS.TYPE.LABEL',
        valueGetter: 'data.documentType && data.documentType.description'
      }
    ];

    _.forEach(colDefs, (colDef:IColDef) => {
      colDef.headerName = this.translationService.instant(colDef.headerName);
    });

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.getColDefs(),
      onGridReady: ():void => {
        this.loadData();
      },
      onRowDataUpdated: ():void => {
        this.gaAgGridService.sizeColumnsToFit(this.gridId);
      },
      domLayout: AgGridDomLayout.AUTO_HEIGHT,
      suppressMovableColumns: true
    };

    this.gridInit = true;
  }

  private loadData = ():void => {
    this.bpProfilesDocumentsDataService.getDocumentsInfo(this.bpProfilesService.activeProfile?.id)
      .then((documentInfo:IDocumentInfo) => {
        this.documentInfo = documentInfo;
        if (util.isNullOrUndefinedOrEmpty(this.documentInfo.caveatsDocumentsCount?.count)) {
          this.documentInfo.caveatsDocumentsCount = { count: 0 };
        }
        this.backupMissingDocumentTypes = util.copyAndExtend(documentInfo?.missingDocumentTypes);
        // update grid data
        this.refreshGridRowData();
      });
  };

  private refreshGridRowData():void {
    this.gaAgGridService.setRowData(this.gridId, this.documentInfo?.bpProfileDocuments);
    // update row pinned bottom data (caveats counter)
    if (this.documentInfo?.caveatsDocumentsCount?.count) {
      this.gaAgGridService.setPinnedBottomRowData(this.gridId, [{
        caveatsCount: this.documentInfo?.caveatsDocumentsCount?.count
      }]);
    }
  }

  private getBPDocumentIndex(id:number):number {
    return _.findIndex(this.documentInfo.bpProfileDocuments, { id });
  }

  private showDocumentDialog(isAdding:boolean, model:Partial<IDocument>):void {
    const dialog:IDialog = this.messageService.showDialog({
      component: AddEditDocumentDialogComponent,
      size: DialogSize.MD,
      wrapperComponent: false,
      inputs: {
        bpProfileId: this.bpProfilesService.activeProfile?.id,
        adding: isAdding,
        entity: 'BPPROFILES.DOCUMENTS.DOCUMENT.LABEL',
        model
      },
      outputs: {
        onSaved: (doc:IDocument):void => {
          const missingDocumentIndex:number = _.findIndex(this.documentInfo.missingDocumentTypes, { code: doc?.documentType?.code });
          // if 'missingDocumentIndex' is existed then we need to remove missing document and add it to the grid instead
          if (missingDocumentIndex >= 0) {
            this.documentInfo.missingDocumentTypes.splice(missingDocumentIndex, 1);
          }
          // add or update grid row data
          if (isAdding) {
            this.documentInfo.bpProfileDocuments.push(doc);
            // increment caveats count
            if (doc.documentType?.code === DocumentTypeCode.CAVEATS) {
              this.documentInfo.caveatsDocumentsCount.count += 1;
            }
          } else {
            const dataIndex:number = this.getBPDocumentIndex(doc?.id);
            this.documentInfo.bpProfileDocuments[dataIndex] = util.copyAndExtend(doc);
          }

          this.refreshGridRowData();
          dialog.hide();
        },
        onCancel: () => dialog.hide()
      }
    });
  }

  public onAddDocument(documentType?:IDocumentType):void {
    this.showDocumentDialog(true, { documentType });
  }

  private onEditDocument = (params:IAgButtonDropdownRendererParams):void => {
    this.showDocumentDialog(false, params.data);
  };

  private onDeleteDocument = (params:IAgButtonDropdownRendererParams):void => {
    const dialogButtons:IDialogButton[] = [
      {
        label: 'GA.BUTTONS.DELETE',
        className: 'btn btn-danger',
        onClick: (updateBusyButtonStatus:DialogBusyButtonFn):void => {
          updateBusyButtonStatus(true);
          this.bpProfilesDocumentsDataService.deleteDocument(this.bpProfilesService.activeProfile?.id, params.data?.id)
            .then(() => {
              // remove data from current documents
              const dataIndex:number = this.getBPDocumentIndex(params.data?.id);
              this.documentInfo.bpProfileDocuments.splice(dataIndex, 1);

              const missingDocumentType:IDocumentType = _.findWhere(this.backupMissingDocumentTypes, { code: (<IDocument>params.data).documentType?.code });
              // if we deleted the required document type then we need to mark it as missing again
              if (missingDocumentType) {
                this.documentInfo.missingDocumentTypes.push(missingDocumentType);
              }
              // decrement caveats count
              if ((<IDocument>params.data).documentType?.code === DocumentTypeCode.CAVEATS) {
                this.documentInfo.caveatsDocumentsCount.count = Math.max(0, this.documentInfo.caveatsDocumentsCount.count - 1);
              }
              // refresh grid data
              this.refreshGridRowData();
              dialog.hide();
            })
            .finally(() => {
              updateBusyButtonStatus(false);
            });
        }
      },
      {
        label: this.translationService.instant('GA.BUTTONS.DONT_LABEL', { label: 'GA.BUTTONS.DELETE' }),
        className: 'btn btn-success',
        onClick: ():void => dialog.hide()
      }
    ];

    const dialog:IDialog = this.messageService.showMessageDialog(
      'GA.GENERAL.CONFIRM_DELETE',
      this.translationService.instant('BPPROFILES.DOCUMENTS.CONFIRM_DELETE_MESSAGE', { fileName: params.data?.description }),
      dialogButtons,
      undefined,
      undefined,
      DialogSize.MD
    );
  };
}